import type React from "react";
import { ViewId } from "@msidentity/SISU/constants";
import { useNavigateDirection, useRedirect } from "@msidentity/SISU/hooks";
import UsernameRecoveryConfig from "../../../username-recovery-config";
import {
  type UsernameRecoveryProofState,
  useUsernameRecoveryContext,
} from "../../../username-recovery-context";
import { UsernameRecoveryActionType } from "../../../username-recovery-reducer";
import {
  type RecoveryResultsStringsFabric,
  useRecoveryResultsStringsFabric,
} from "./fabric/use-recovery-results-strings-fabric";

export type RecoveryResultsState = RecoveryResultsStringsFabric & {
  proofState: UsernameRecoveryProofState;
  onSubmitHandler: (event: React.SyntheticEvent) => void;
};

/**
 * @returns handler for calling useRecoveryResults.
 */
export const useRecoveryResults = function useRecoveryResults(): RecoveryResultsState {
  const { successUrl } = UsernameRecoveryConfig.instance;

  const {
    viewState: { proofState },
    dispatchStateChange,
  } = useUsernameRecoveryContext();
  const { isSignInAllowedProof, hasAssociatedAccounts } = proofState;
  const redirect = useRedirect();
  const navigate = useNavigateDirection();

  // If we have neither, there are no associated accounts
  const noAssociatedAccounts = !isSignInAllowedProof && !hasAssociatedAccounts;

  const onSubmitHandler = (event: React.SyntheticEvent) => {
    event.preventDefault();

    if (noAssociatedAccounts) {
      // If there are no associated accounts, we'll clear the prefill so the user isn't stuck in a loop
      dispatchStateChange({ type: UsernameRecoveryActionType.SetHasPrefill, hasPrefill: false });
      navigate(ViewId.UsernameRecoveryResult, ViewId.UsernameRecoveryCollect);
    } else {
      redirect(successUrl, false);
    }
  };

  const viewStrings = useRecoveryResultsStringsFabric(noAssociatedAccounts);

  return {
    ...viewStrings,
    proofState,
    onSubmitHandler,
  };
};
